import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Slider from '@material-ui/core/Slider';
import Tooltip from '@material-ui/core/Tooltip';
import {
  VolumeDown,
  VolumeOff,
  VolumeUp,
} from '@material-ui/icons';
import { observer } from 'mobx-react';

import { translate } from 'services/i18nService';
import SessionStorageService from 'services/SessionStorageService';

import styles from './VolumeControl.css';


const HALF_VOLUME = 50;

const VolumeControl = (props) => {
  const {
    handleVolume,
    playerService,
    videoAndChatWrapper,
    isJinkPlayer,
    jinkPlayer,
    volume,
    onVolumeChange,
  } = props;

  const checkCallbacks = useCallback((prop) => prop && typeof prop === 'function', []);

  const getSliderValue = useCallback(() => {
    if (isJinkPlayer) {
      return jinkPlayer.muted() ? 0 : volume * 100;
    }

    return playerService.isVideoMuted ? 0 : playerService.volume;
  }, [isJinkPlayer, jinkPlayer, playerService.isVideoMuted, playerService.volume, volume]);

  const getVolumeValue = useCallback(() => {
    if (isJinkPlayer) {
      return volume * 100;
    }

    return playerService.volume;
  }, [isJinkPlayer, playerService.volume, volume]);

  const isMuted = useCallback(() => {
    if (isJinkPlayer) {

      return jinkPlayer.muted();
    }

    return playerService.isVideoMuted;
  }, [isJinkPlayer, jinkPlayer, playerService.isVideoMuted]);

  const onMuteVideo = useCallback(() => {
    if (isJinkPlayer) {
      if (!jinkPlayer.muted()) {
        jinkPlayer.muted(true);
        handleVolume(0);
        SessionStorageService.set('profileVideoMuted', true);
      } else {
        jinkPlayer.muted(false);
        handleVolume();
        SessionStorageService.set('profileVideoMuted', false);
      }
    } else {
      playerService.toggleMuted();
    }
  }, [handleVolume, isJinkPlayer, jinkPlayer, playerService]);

  const handleVolumeChange = useCallback((event, value) => {
    if (isJinkPlayer) {
      handleVolume(value / 100);
      jinkPlayer.muted(!value);
    } else {
      playerService.setVolume(value);
      if (checkCallbacks(onVolumeChange)) {
        onVolumeChange(value);
      }
    }
  }, [checkCallbacks, handleVolume, isJinkPlayer, jinkPlayer, onVolumeChange, playerService]);

  const volumeIcon = useMemo(() => {
    const popperProps = { container: videoAndChatWrapper };

    if (getVolumeValue() < HALF_VOLUME && !isMuted()) {
      return (
        <Tooltip
          PopperProps={popperProps}
          placement="top"
          title={translate('v2profile.player.controls.mute.stream')}
        >
          <VolumeDown
            className={styles.volumeControlsItem}
            onClick={onMuteVideo}
          />
        </Tooltip>
      );
    }
    if (getVolumeValue() === 0 || isMuted()) {
      return (
        <Tooltip
          PopperProps={popperProps}
          placement="top"
          title={translate('v2profile.player.controls.unmute.stream')}
        >
          <VolumeOff
            className={styles.volumeControlsItem}
            onClick={onMuteVideo}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip
        PopperProps={popperProps}
        placement="top"
        title={translate('v2profile.player.controls.mute.stream')}
      >
        <VolumeUp
          className={styles.volumeControlsItem}
          onClick={onMuteVideo}
        />
      </Tooltip>
    );
  }, [isMuted, onMuteVideo, videoAndChatWrapper, getVolumeValue]);

  const sliderClasses = [
    styles.volumeSlider,
    styles.volumeControlsItem,
  ];

  return (
    <div className={styles.volumeControls}>
      {volumeIcon}
      <Slider
        aria-labelledby="volume-slider"
        className={classNames(...sliderClasses)}
        classes={{
          track: styles.volumeSliderTrack,
          thumb: styles.volumeSliderThumb,
          rail: styles.volumeSliderRail,
        }}
        value={getSliderValue()}
        onChange={handleVolumeChange}
      />
    </div>
  );
};

VolumeControl.propTypes = {
  isJinkPlayer: PropTypes.bool.isRequired,
  jinkPlayer: PropTypes.object.isRequired,
  playerService: PropTypes.object.isRequired,
  handleVolume: PropTypes.func,
  videoAndChatWrapper: PropTypes.object,
  volume: PropTypes.number,
  onVolumeChange: PropTypes.func,
};

VolumeControl.defaultProps = {
  volume: HALF_VOLUME,
  videoAndChatWrapper: {},
  handleVolume: () => undefined,
  onVolumeChange: () => undefined,
};


export default observer(VolumeControl);
